import { components, paths } from "@/api-schema";
import {
  DataExportType,
  PathsV1BranchSearchPostParametersQueryExtend,
  PathsV1BranchSearchPostParametersQueryOrderByDirection,
  PathsV1BranchSearchPostParametersQueryOrderByField,
} from "@/api-schema-enums";
import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import {
  BRANCH_DELETE_CUSTOM_ERRORS,
  resolveBranchDeleteErrorMessage,
} from "@/common/api/branch/_helpers/branch-delete-error";
import { useBranchDeleteMutation } from "@/common/api/branch/use-branch-delete.mutation";
import { useBranchRestoreMutation } from "@/common/api/branch/use-branch-restore.mutation";
import { useBranchSearchQuery } from "@/common/api/branch/use-branch-search.query";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { ImplementedColumnDef, TableFeatures } from "@/components/data-table/types";
import { DeletedStatus, DeletedStatusFilter } from "@/components/filter/predefined/deleted-status-filter";
import i18next from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { createBasePredefinedTable } from "./_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  DataFetchHooks,
  PredefinedTableConfig,
} from "./_components/base-predefined-table.types";
import { commonColumns } from "./_constants/common-columns";
import { branchTableDefaultColumns } from "./branch-table-columns";

type Branch = components["schemas"]["Branch"] & RowWithIdentifier;
type BranchSorting = NonNullable<paths["/v1/branch/search"]["post"]["parameters"]["query"]["orderBy"]>;
type BranchQueryParams = paths["/v1/branch/search"]["post"]["parameters"]["query"];
type BranchRequestBody = paths["/v1/branch/search"]["post"]["requestBody"]["content"]["application/json"];
type BranchExtend = NonNullable<paths["/v1/branch/search"]["post"]["parameters"]["query"]["extend"]>;

const DEFAULT_EXTEND: BranchExtend = [
  PathsV1BranchSearchPostParametersQueryExtend.Address,
  PathsV1BranchSearchPostParametersQueryExtend.Company,
  PathsV1BranchSearchPostParametersQueryExtend.Brands,
  PathsV1BranchSearchPostParametersQueryExtend.BranchIdentifiers,
] as const;

const createDefaultColumns = (showDistance: boolean): ImplementedColumnDef<Branch, unknown>[] => {
  return [
    branchTableDefaultColumns.columnName,
    branchTableDefaultColumns.columnV1Id,
    branchTableDefaultColumns.columnCompany,
    branchTableDefaultColumns.columnAddress,
    branchTableDefaultColumns.columnBrands,
    ...(showDistance ? [commonColumns.columnDistance<Branch>()] : []),
    branchTableDefaultColumns.columnTechnicalId,
    branchTableDefaultColumns.columnDeletedAt,
  ] as ImplementedColumnDef<Branch, unknown>[];
};

const DEFAULT_COLUMNS = createDefaultColumns(false);
const DEFAULT_COLUMNS_WITH_DISTANCE = createDefaultColumns(true);

const BRANCH_TABLE_CONFIG: PredefinedTableConfig<BranchSorting> = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  defaultSorting: [
    {
      direction: PathsV1BranchSearchPostParametersQueryOrderByDirection.Asc,
      field: PathsV1BranchSearchPostParametersQueryOrderByField.Company,
    },
    {
      direction: PathsV1BranchSearchPostParametersQueryOrderByDirection.Asc,
      field: PathsV1BranchSearchPostParametersQueryOrderByField.Brands,
    },
    {
      direction: PathsV1BranchSearchPostParametersQueryOrderByDirection.Asc,
      field: PathsV1BranchSearchPostParametersQueryOrderByField.Address,
    },
  ] as const,
  defaultTableConfig: {
    searchPlaceholder: i18next.t("common:SearchBranches"),
    features: {
      multiSelection: false,
      pagination: true,
      search: true,
      expansion: false,
    } as TableFeatures,
  } as const,
  defaultRowActions: {
    showEdit: true,
    showDelete: true,
    showView: true,
  } as const,
  sortingMapping: {
    label: ["company", "brands", "address"],
    address: ["address"],
    company: ["company"],
    deletedAt: ["deletedAt"],
    createdAt: ["createdAt"],
    updatedAt: ["updatedAt"],
  } as const,
  exportType: DataExportType.Branch,
} as const;

const BRANCH_TABLE_QUERY_HOOKS: DataFetchHooks<Branch, BranchQueryParams, BranchRequestBody> = {
  useSearchQuery: useBranchSearchQuery,
  useDeleteMutation: useBranchDeleteMutation,
  useRestoreMutation: useBranchRestoreMutation,
} as const;

export interface BranchTableProps extends BasePredefinedTableProps<
  Branch,
  BranchSorting,
  BranchQueryParams,
  BranchRequestBody
> {
  extend?: BranchExtend;
  showDistance?: boolean;
}

const BaseBranchTable = createBasePredefinedTable({
  config: BRANCH_TABLE_CONFIG,
  queryHooks: BRANCH_TABLE_QUERY_HOOKS,
  defaultColumns: DEFAULT_COLUMNS,
});

export function BranchTable(props: BranchTableProps) {
  const { t } = useTranslation(["common"]);
  const {
    name = "branch-table",
    extend = DEFAULT_EXTEND,
    showCreateButton = true,
    body = { deletedAt: [DeletedStatus.ACTIVE, DeletedStatus.DELETED] },
    filters,
    filterValues,
    columns,
    showDistance = false,
    ...restProps
  } = props;

  const defaultFilters = useMemo(() => filters ?? [new DeletedStatusFilter()], [filters]);
  const defaultFilterValues = useMemo(() => filterValues ?? { deletedAt: [DeletedStatus.ACTIVE] }, [filterValues]);

  return (
    <BaseBranchTable
      searchPlaceholder={t("common:SearchBranches")}
      {...{
        name,
        extend,
        showCreateButton,
        body,
        columns: columns ?? (showDistance ? DEFAULT_COLUMNS_WITH_DISTANCE : DEFAULT_COLUMNS),
        filters: defaultFilters,
        filterValues: defaultFilterValues,
        confirmActions: ["delete", "restore"],
        toastMessages: {
          deleteSuccess: t("BranchHasBeenDeleted"),
          deleteError: t("DeleteBranchError"),
          deleteCustomErrors: BRANCH_DELETE_CUSTOM_ERRORS,
          deleteResolveErrorMessage: resolveBranchDeleteErrorMessage,
          restoreSuccess: t("BranchHasBeenRestored"),
          restoreError: t("RestoreBranchError"),
        },
        confirmLabels: {
          delete: (row: Branch) => ({
            title: row.deletedAt ? t("DeleteBranchPermanently") : t("DeleteBranch"),
            text: row.deletedAt ? t("DeleteBranchPermanentlyDescription") : t("DeleteBranchDescription"),
            confirmButton: row.deletedAt ? t("YesDeletePermanently") : t("ram:YesDelete"),
            cancelButton: t("Cancel"),
          }),
          restore: {
            title: t("RestoreBranch"),
            text: t("RestoreBranchDescription"),
            confirmButton: t("YesRestore"),
            cancelButton: t("Cancel"),
          },
        },
        ...restProps,
      }}
    />
  );
}
